// =============================================================================
// shipGate — single source of truth for "can this order be shipped?"
//
// Both the OrderView header button and the ShipmentEntry Stage-1 confirm button
// gate on the same rules; they used to carry copy-pasted (and drifting) copies.
// The rules live here once. Only the wording differs by surface: "short" reads
// as a button tooltip, "long" as a Stage-1 alert body.
// =============================================================================

// A line/release is shippable when the release is open, not voided, and still
// has quantity left to ship. Non-stock line types (comments, kit parents with
// no inventory) fall out naturally — they carry no open release with qty.
export function buildShippableRows(order) {
  const rows = [];
  const lines = (order && order.lines) || [];
  for (const ln of lines) {
    if (ln.voidLine) continue;
    const partNum = ln.partNum || "";
    const releases = Array.isArray(ln.releases) ? ln.releases : [];
    // Fall back to a single synthetic release when Epicor didn't nest any
    // (rare, but keeps a shippable row visible instead of hiding the line).
    const rels = releases.length ? releases : [{
      orderRelNum: 1, warehouseCode: "", ourReqQty: ln.orderQty,
      ourStockShippedQty: 0, openRelease: ln.openLine,
    }];
    for (const rel of rels) {
      if (rel.voidRelease) continue;
      const open = rel.openRelease !== false;
      if (!open) continue;
      const reqQty = Number(rel.ourReqQty ?? ln.orderQty ?? 0);
      const shippedQty = Number(rel.ourStockShippedQty ?? 0) + Number(rel.ourJobShippedQty ?? 0);
      const remaining = Math.max(0, reqQty - shippedQty);
      if (remaining <= 0) continue;
      rows.push({
        key: `${ln.orderLine}-${rel.orderRelNum}`,
        orderLine: ln.orderLine,
        orderRelNum: rel.orderRelNum,
        partNum,
        lineDesc: ln.lineDesc || ln.partNumPartDescription || "",
        warehouseCode: rel.warehouseCode || "",
        remaining,
        uom: ln.ium || ln.invtyUOM || rel.ium || "EA",
        unitPrice: Number(ln.docUnitPrice ?? ln.unitPrice ?? 0),
      });
    }
  }
  return rows;
}

// True only when we can *prove* nothing is left to ship: the order carries at
// least one non-void line, and no line/release has remaining quantity. An order
// with no lines loaded yet is "unknown", never "fully shipped" — otherwise the
// button would blink disabled during the fetch.
export function isFullyShipped(order) {
  const lines = (order && order.lines) || [];
  if (!Array.isArray(lines) || lines.length === 0) return false;
  if (lines.every((ln) => ln.voidLine)) return false;
  return buildShippableRows(order).length === 0;
}

// Shipping progress across the whole order, for display. Unlike the gate
// above this counts *every* non-void release, open or not, because a closed
// release still represents quantity that did (or didn't) go out.
//   "none" | "partial" | "full" | "unknown"
// "unknown" when lines haven't loaded or carry no quantity to measure against.
export function shipProgress(order) {
  const lines = (order && order.lines) || [];
  if (!Array.isArray(lines) || lines.length === 0) return "unknown";

  let reqTotal = 0, shippedTotal = 0, counted = 0;
  for (const ln of lines) {
    if (ln.voidLine) continue;
    const releases = Array.isArray(ln.releases) ? ln.releases : [];
    const rels = releases.length ? releases : [{
      ourReqQty: ln.orderQty, ourStockShippedQty: 0, ourJobShippedQty: 0,
    }];
    for (const rel of rels) {
      if (rel.voidRelease) continue;
      counted += 1;
      reqTotal += Number(rel.ourReqQty ?? ln.orderQty ?? 0);
      shippedTotal += Number(rel.ourStockShippedQty ?? 0) + Number(rel.ourJobShippedQty ?? 0);
    }
  }

  if (counted === 0 || reqTotal <= 0) return "unknown";
  if (shippedTotal <= 0) return "none";
  return shippedTotal >= reqTotal ? "full" : "partial";
}

// Why this order can't be shipped, or null when it can. Epicor otherwise
// rejects held / credit-held / closed orders deep inside pack creation with a
// cryptic "…not found or returned no header" error, so surface the real reason
// up front. `variant` picks the wording register, not the rules.
export function shipBlockReason(order, variant = "short") {
  if (!order) return null;
  const header = order.header || {};
  const long = variant === "long";

  // A per-order Credit Override (granted in Epicor's Credit Manager) releases
  // THIS order from the customer's credit hold — Epicor allows packing such an
  // order, so the gate must too. Only block when the customer is held AND no
  // override was granted for the order.
  if (header.custOnCreditHold && !header.creditOverride) {
    return long
      ? "This order is on Credit Hold. A shipment can't be created until the credit hold is released in Epicor."
      : "Order is on Credit Hold — release it in Epicor before it can be shipped.";
  }
  if (header.orderHeld) {
    return long
      ? "This order is Held. Release the order hold in Epicor before shipping."
      : "Order is Held — release the hold in Epicor before shipping.";
  }
  if (header.openOrder === false) {
    return long
      ? "This order is Closed. It has to be re-opened before a shipment can be created."
      : "Order is Closed — re-open it before a shipment can be created.";
  }
  // Epicor leaves OpenOrder true until the order is invoiced, so a fully
  // shipped order still looks open. Check the release quantities instead.
  if (isFullyShipped(order)) {
    // "Nothing left to ship" rather than "fully shipped": an order can also
    // land here with a closed release that never shipped its remainder, and
    // the Shipped badge would read "Partially shipped" in that case.
    return long
      ? "There is nothing left to ship on this order — every open release has its full quantity shipped."
      : "Nothing left to ship — every open release is fully shipped.";
  }
  return null;
}
